import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  Bell, 
  MessageCircle, 
  LogOut,
  ChevronDown,
  Settings,
  UserCircle,
  Shield,
  LayoutDashboard,
  Bot
} from "lucide-react";
import { signOut } from "firebase/auth";
import { auth } from "../../Services/Firebase/FirebaseIndex";
import logo from "../../assets/Logo/GIGiFY Logo Horizontal no tagline.png";
import talentIcon from "../../assets/Icons/IconTalent.png";
import organiserIcon from "../../assets/Icons/IconOrganiser.png";
import adminIcon from "../../assets/Icons/IconAdmin.png";
import { useLoading } from "../GlobalLoading/LoadingContext";
import { useAuth } from "../Auth/AuthContext";
import { useUserProfile } from '../Auth/UserProfileContext';
import { useResponsiveBreakpoints } from "../../hooks/useResponsiveBreakpoints";
import { usePerformanceMonitor } from "../../hooks/usePerformanceMonitor";
import { prefersReducedMotion } from "../../utils/browserCompatibility";
import debug from '../../utils/debug';
import HamburgerButton from '../ui/HamburgerButton';
import { useGlobalMessaging } from '../../hooks/useMessaging';
import ChatBot from '../ChatBot/GigBot';
import NotificationService from '../../Services/Notifications/NotificationService';

// Debounce utility function
const debounceUtil = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Constants for notification cache
const NOTIFICATION_CACHE_KEY = 'gigify_notif_counts';
const NOTIFICATION_TIMESTAMP_KEY = 'gigify_notif_timestamp';
const NOTIFICATION_TTL = 3 * 60 * 1000; // 3 minutes

const UnifiedHeader = React.memo(() => {
  // Performance monitoring
  const { startTiming, endTiming } = usePerformanceMonitor({
    componentName: 'UnifiedHeader',
    enableCustomMetrics: true
  });

  // Context hooks
  const { currentUser, isVerified, userType, authReady } = useAuth();
  const { profile, profileDisplayName, profilePhotoURL, loading: profileLoading } = useUserProfile();
  const { isMobile, width } = useResponsiveBreakpoints();
  const { totalUnreadCount: messagingUnreadCount } = useGlobalMessaging();
  
  // Simplified 2-breakpoint system
  const [screenSize, setScreenSize] = useState(() => {
    const width = window.innerWidth;
    return width <= 1000 ? 'mobile' : 'desktop';
  });

  // Mobile/hamburger menu detection - anything 1000px and below
  const isMobileLayout = width <= 1000;
  const { setIsLoading, showLoading, hideLoading } = useLoading();
  
  // Router hooks
  const location = useLocation();
  const navigate = useNavigate();

  // State management
  const [isScrolled, setIsScrolled] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({ notifications: 0, messages: 0 });
  const [hasCheckedCache, setHasCheckedCache] = useState(false);
  const [chatBotOpen, setChatBotOpen] = useState(false);

  // Derived state
  const isAuthenticated = !!currentUser;
  const userId = currentUser?.uid || "";
  // Only use profileDisplayName from Firestore - no fallbacks
  const userName = profileDisplayName || "";
  const isAdmin = useMemo(() => {
    return location.pathname.includes("/admin-") || userType === "Admin";
  }, [location.pathname, userType]);

  // Simplified header type logic - only public or authenticated
  const headerType = useMemo(() => {
    if (!isAuthenticated) return 'public';
    return 'authenticated';
  }, [isAuthenticated]);

  // Color constants
  const COLORS = useMemo(() => ({
    primary: "#01a2f1",
    secondary: "#f85008",
    tertiary: "#144874",
    white: "#fff",
    headerBg: "rgba(20, 72, 116, 0.65)",
    textDark: "#144874",
    hover: "#1483c5",
    registerHover: "#0f3a5f",
    talentAccent: "rgba(1, 162, 241, 0.2)",
    organiserAccent: "rgba(248, 80, 8, 0.2)",
    adminAccent: "rgba(85, 85, 85, 0.2)",
  }), []);

  // Icon size constants
  const ICON_SIZES = {
    regular: 20,
    notification: 22,
  };

  // Helper to get cached notification counts
  const getCachedNotifications = useCallback((userId) => {
    try {
      if (!userId) return null;
      
      const cacheKey = `${NOTIFICATION_CACHE_KEY}_${userId}`;
      const timestampKey = `${NOTIFICATION_TIMESTAMP_KEY}_${userId}`;
      
      const countsJson = sessionStorage.getItem(cacheKey);
      const timestampStr = sessionStorage.getItem(timestampKey);
      
      if (!countsJson || !timestampStr) return null;
      
      const timestamp = parseInt(timestampStr, 10);
      const now = Date.now();
      
      if (timestamp && now < timestamp + NOTIFICATION_TTL) {
        const cachedCounts = JSON.parse(countsJson);
        debug.log('UnifiedHeader', 'Using cached notification counts', cachedCounts);
        return cachedCounts;
      }
      
      return null;
    } catch (error) {
      debug.error('UnifiedHeader', 'Error getting cached notifications', error);
      return null;
    }
  }, []);

  // Helper to cache notification counts
  const cacheNotifications = useCallback((userId, counts) => {
    try {
      if (!userId || !counts) return;
      
      const cacheKey = `${NOTIFICATION_CACHE_KEY}_${userId}`;
      const timestampKey = `${NOTIFICATION_TIMESTAMP_KEY}_${userId}`;
      
      sessionStorage.setItem(cacheKey, JSON.stringify(counts));
      sessionStorage.setItem(timestampKey, Date.now().toString());
      
      debug.log('UnifiedHeader', 'Cached notification counts', counts);
    } catch (error) {
      debug.error('UnifiedHeader', 'Error caching notifications', error);
    }
  }, []);

  // Fetch unread notification counts
  const fetchUnreadCounts = useCallback(async (userId, type) => {
    if (!userId || !type || isAdmin) {
      return { notifications: 0, messages: 0 };
    }

    try {
      // First check cache
      if (!hasCheckedCache) {
        const cachedCounts = getCachedNotifications(userId);
        if (cachedCounts) {
          setUnreadCounts(cachedCounts);
          setHasCheckedCache(true);
          return cachedCounts;
        }
        setHasCheckedCache(true);
      }

      debug.log('UnifiedHeader', 'Fetching unread counts for', { userId, type });
      
      // Get unread notifications count using NotificationService
      const notificationResult = await NotificationService.getUserNotifications(
        userId, 
        50, // Get more notifications to count unread
        null, 
        type
      );
      
      const notificationCount = notificationResult.success 
        ? (notificationResult.notifications || []).filter(n => !n.isRead).length 
        : 0;
      
      // Use the new messaging system's unread count
      const messageCount = messagingUnreadCount || 0;
      
      const results = { notifications: notificationCount, messages: messageCount };

      setUnreadCounts(results);
      cacheNotifications(userId, results);
      debug.log('UnifiedHeader', 'Unread counts fetched', results);
      
      return results;
    } catch (error) {
      debug.error('UnifiedHeader', 'Error fetching unread counts', error);
      return { notifications: 0, messages: 0 };
    }
  }, [isAdmin, hasCheckedCache, getCachedNotifications, cacheNotifications, messagingUnreadCount]);


  // Scroll handler
  useEffect(() => {
    startTiming('header-scroll-setup');

    const handleScroll = debounceUtil(() => {
      const scrolled = window.scrollY > 50;
      if (scrolled !== isScrolled) {
        setIsScrolled(scrolled);
      }
    }, 50);

    window.addEventListener("scroll", handleScroll, { passive: true });
    
    endTiming('header-scroll-setup');
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isScrolled, startTiming, endTiming]);


  // Responsive breakpoints handler
  useEffect(() => {
    const handleResize = debounceUtil(() => {
      const width = window.innerWidth;
      const newScreenSize = width <= 1000 ? 'mobile' : 'desktop';
      
      if (newScreenSize !== screenSize) {
        setScreenSize(newScreenSize);
      }
    }, 100);
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [screenSize]);

  // Click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest(".user-menu")) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  // Profile photo URL now comes directly from UserProfileContext - simplified and stable
  const userProfilePhoto = profilePhotoURL;

  // Fetch unread counts for authenticated users
  useEffect(() => {
    if (isAuthenticated && userType && authReady && !isAdmin && headerType === 'authenticated') {
      fetchUnreadCounts(userId, userType);
      
      const intervalId = setInterval(() => {
        fetchUnreadCounts(userId, userType);
      }, 5 * 60 * 1000);
      
      return () => clearInterval(intervalId);
    }
  }, [isAuthenticated, userType, authReady, isAdmin, headerType, userId, fetchUnreadCounts]);

  // Update message count when messaging system updates
  useEffect(() => {
    if (isAuthenticated && !isAdmin) {
      setUnreadCounts(prev => ({
        ...prev,
        messages: messagingUnreadCount || 0
      }));
    }
  }, [messagingUnreadCount, isAuthenticated, isAdmin]);

  // Event handlers
  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen(prev => !prev);
    setActiveDropdown(null);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  }, []);

  const toggleDropdown = useCallback((dropdownName) => {
    setActiveDropdown(prev => prev === dropdownName ? null : dropdownName);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      // Show loading immediately to prevent error flash
      showLoading("Signing out...", "logout");
      debug.log('UnifiedHeader', 'Logging out user');
      
      // Navigate first to prevent error flash during auth state change
      navigate("/logged-out");
      
      // Small delay to ensure navigation starts
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Then sign out
      await signOut(auth);
      setShowDropdown(false);
    } catch (error) {
      debug.error('UnifiedHeader', 'Error signing out', error);
      hideLoading("logout");
    }
  }, [showLoading, hideLoading, navigate]);

  // Hover handlers for buttons
  const handleButtonHover = useCallback((isHovering, buttonType) => {
    if (prefersReducedMotion()) return () => {};
    
    return (e) => {
      if (buttonType === 'login') {
        e.currentTarget.style.backgroundColor = isHovering ? COLORS.hover : COLORS.primary;
        e.currentTarget.style.transform = isHovering ? "translateY(-2px)" : "translateY(0)";
        e.currentTarget.style.boxShadow = isHovering 
          ? "0 4px 10px rgba(1, 162, 241, 0.3)"
          : "0 2px 6px rgba(1, 162, 241, 0.2)";
      } else if (buttonType === 'register') {
        e.currentTarget.style.backgroundColor = isHovering ? COLORS.registerHover : COLORS.tertiary;
        e.currentTarget.style.transform = isHovering ? "translateY(-2px)" : "translateY(0)";
        e.currentTarget.style.boxShadow = isHovering 
          ? "0 4px 10px rgba(20, 72, 116, 0.3)"
          : "0 2px 6px rgba(20, 72, 116, 0.2)";
      }
    };
  }, [COLORS]);

  const handleLogoHover = useCallback((isHovering) => {
    if (prefersReducedMotion()) return;
    
    return (e) => {
      e.currentTarget.style.transform = isHovering ? "scale(1.05)" : "scale(1)";
    };
  }, []);

  // Get logo link destination - always go to home page
const getLogoLink = useCallback(() => {
  return "/";
}, []);

  // Get dashboard URL
  const getDashboardUrl = useCallback(() => {
    if (isAdmin) return "/admin-dashboard";
    if (userType === "Talent") return `/talent-dashboard/${userId}`;
    return `/organiser-dashboard/${userId}`;
  }, [isAdmin, userType, userId]);

  // Get user icon
  const getUserIcon = useCallback(() => {
    if (isAdmin) return adminIcon;
    return userType === "Talent" ? talentIcon : organiserIcon;
  }, [isAdmin, userType]);

  // Get theme colors based on user type
  const getThemeColors = useCallback(() => {
    if (isAuthenticated && userType === "Talent") {
      return {
        primary: COLORS.primary,
        accent: COLORS.talentAccent
      };
    }
    if (isAuthenticated && userType === "Organiser") {
      return {
        primary: COLORS.secondary,
        accent: COLORS.organiserAccent
      };
    }
    return {
      primary: COLORS.primary,
      accent: COLORS.talentAccent
    };
  }, [isAuthenticated, userType, COLORS]);

  const themeColors = getThemeColors();

  // Get responsive spacing and sizing - Simplified 2-breakpoint system
  const getResponsiveConfig = () => {
    if (screenSize === 'mobile') {
      // Mobile breakpoint: Linear scale from 1000px (100%) to 300px (80%)
      let mobileScale;
      if (width >= 1000) {
        mobileScale = 1; // 100% at 1000px
      } else if (width <= 300) {
        mobileScale = 0.8; // 80% at 300px
      } else {
        // Simple linear interpolation from 100% to 80% over 700px range
        mobileScale = 0.8 + (width - 300) * 0.2 / 700;
      }
      
      return {
        navGap: `${0.8 * mobileScale}rem`,
        navPadding: `${0.35 * mobileScale}rem ${0.5 * mobileScale}rem`,
        fontSize: `${0.9 * mobileScale}rem`,
        logoHeight: `${57.8 * mobileScale}px`, // Mobile logo baseline reduced by 42% total from original 100px
        dashboardWidth: `${75 * mobileScale}px`,
        dashboardHeight: `${65 * mobileScale}px`,
        userMenuPadding: `${0.35 * mobileScale}rem ${0.6 * mobileScale}rem`,
        buttonPadding: `${0.4 * mobileScale}rem ${1.2 * mobileScale}rem`,
        buttonFontSize: `${0.85 * mobileScale}rem`,
        buttonMinHeight: `${38 * mobileScale}px`,
        buttonMinWidth: `${100 * mobileScale}px`,
        profileImageSize: `${40 * mobileScale}px`,
        iconSize: Math.round(18 * mobileScale),
        navOffset: '85px', // Not used in mobile but kept for consistency
        headerPadding: `${0.3 * mobileScale}rem ${1 * mobileScale}rem`,
        headerHeight: `${70 * mobileScale}px`
      };
    } else {
      // Desktop breakpoint with enhanced scaling
      const desktopScale = width >= 1900 ? 1 : Math.max(0.6, 0.6 + (width - 1001) * 0.4 / 899);
      
      return {
        navGap: `${1.4 * desktopScale}rem`,
        navPadding: `${0.5 * desktopScale}rem ${0.8 * desktopScale}rem`,
        fontSize: `${1 * desktopScale}rem`,
        logoHeight: `${86.7 * desktopScale}px`, // Desktop logo baseline reduced by 42% total from original 150px
        dashboardWidth: `${85 * desktopScale}px`,
        dashboardHeight: `${75 * desktopScale}px`,
        userMenuPadding: `${0.35 * desktopScale}rem ${0.5 * desktopScale}rem`,
        buttonPadding: `${0.4 * desktopScale}rem ${Math.max(0.8, 1.2 * desktopScale)}rem`,
        buttonFontSize: `${Math.min(1.1, 1.1 * desktopScale)}rem`,
        buttonMinHeight: `${42 * desktopScale}px`,
        buttonMinWidth: `${110 * desktopScale}px`,
        userMenuMinHeight: `${55 * desktopScale}px`,
        profileImageSize: `${Math.min(64, 64 * desktopScale)}px`, // Cap at 64px max
        userNameFontSize: `${Math.min(1.25, 1.25 * desktopScale)}rem`,
        userRoleFontSize: `${Math.min(0.9, 0.9 * desktopScale)}rem`,
        userRolePadding: `${0.1 * desktopScale}rem ${0.4 * desktopScale}rem`,
        dropdownFontSize: `${1 * desktopScale}rem`,
        iconSize: Math.round(Math.min(24, 24 * desktopScale)),
        dashboardIconSize: Math.round(30 * desktopScale),
        chevronSize: Math.round(Math.min(20, 20 * desktopScale)),
        navOffset: `${140 * desktopScale}px`, // Scale the navigation offset proportionally
        buttonGap: `${0.75 * desktopScale}rem`, // Gap between login/register buttons
        headerPadding: `0.3rem ${2 * desktopScale}rem`, // Scale horizontal padding
        headerHeight: `${85 * desktopScale}px`,
        rightContentGap: `${1 * desktopScale}rem`, // Gap between right side elements
        userMenuGap: `${0.4 * desktopScale}rem`, // Gap inside user menu
        dashboardGap: `${0.4 * desktopScale}rem` // Gap inside dashboard button
      };
    }
  };

  const responsive = getResponsiveConfig();

  // Render navigation menu for authenticated users
  const renderAuthenticatedNav = () => {
    if (isAdmin) return null;
    
    return (
      <nav className="desktop-nav" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: responsive.navGap,
        height: '100%',
        width: 'max-content'
      }}>
        <Link
          to="/browse-talent"
          className="nav-link"
          data-tour="browse-talent"
          style={{
            color: location.pathname === "/browse-talent" ? COLORS.primary : COLORS.white,
            padding: responsive.navPadding,
            borderRadius: '4px',
            fontSize: responsive.fontSize,
            whiteSpace: 'nowrap',
            transition: prefersReducedMotion() ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: 'scale(1)'
          }}
          onMouseEnter={(e) => {
            if (!prefersReducedMotion()) {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.color = COLORS.primary;
            }
          }}
          onMouseLeave={(e) => {
            if (!prefersReducedMotion() && location.pathname !== "/browse-talent") {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.color = COLORS.white;
            } else if (location.pathname === "/browse-talent") {
              e.currentTarget.style.transform = 'scale(1)';
            }
          }}
        >
          Browse Talent
        </Link>

        <Link
          to="/explore-events"
          className="nav-link"
          data-tour="explore-gigs"
          style={{
            color: location.pathname === "/explore-events" ? COLORS.primary : COLORS.white,
            padding: responsive.navPadding,
            borderRadius: '4px',
            fontSize: responsive.fontSize,
            whiteSpace: 'nowrap',
            transition: prefersReducedMotion() ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: 'scale(1)'
          }}
          onMouseEnter={(e) => {
            if (!prefersReducedMotion()) {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.color = COLORS.primary;
            }
          }}
          onMouseLeave={(e) => {
            if (!prefersReducedMotion() && location.pathname !== "/explore-events") {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.color = COLORS.white;
            } else if (location.pathname === "/explore-events") {
              e.currentTarget.style.transform = 'scale(1)';
            }
          }}
        >
          Explore Gigs
        </Link>

        <Link
          to="/how-it-works"
          className="nav-link"
          style={{
            color: (location.pathname === "/how-it-works" || location.pathname.startsWith("/how-it-works")) ? COLORS.primary : COLORS.white,
            padding: responsive.navPadding,
            borderRadius: '4px',
            fontSize: responsive.fontSize,
            whiteSpace: 'nowrap',
            transition: prefersReducedMotion() ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: 'scale(1)'
          }}
          onMouseEnter={(e) => {
            if (!prefersReducedMotion()) {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.color = COLORS.primary;
            }
          }}
          onMouseLeave={(e) => {
            if (!prefersReducedMotion() && !location.pathname.startsWith("/how-it-works")) {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.color = COLORS.white;
            } else if (location.pathname.startsWith("/how-it-works")) {
              e.currentTarget.style.transform = 'scale(1)';
            }
          }}
        >
          How It Works
        </Link>

        <Link
          to="/our-story"
          className="nav-link"
          style={{
            color: location.pathname === "/our-story" ? COLORS.primary : COLORS.white,
            padding: responsive.navPadding,
            borderRadius: '4px',
            fontSize: responsive.fontSize,
            whiteSpace: 'nowrap',
            transition: prefersReducedMotion() ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: 'scale(1)'
          }}
          onMouseEnter={(e) => {
            if (!prefersReducedMotion()) {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.color = COLORS.primary;
            }
          }}
          onMouseLeave={(e) => {
            if (!prefersReducedMotion() && location.pathname !== "/our-story") {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.color = COLORS.white;
            } else if (location.pathname === "/our-story") {
              e.currentTarget.style.transform = 'scale(1)';
            }
          }}
        >
          Our Story
        </Link>

        <Link
          to="/contact"
          className="nav-link"
          style={{
            color: location.pathname === "/contact" ? COLORS.primary : COLORS.white,
            padding: responsive.navPadding,
            borderRadius: '4px',
            fontSize: responsive.fontSize,
            whiteSpace: 'nowrap',
            transition: prefersReducedMotion() ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: 'scale(1)'
          }}
          onMouseEnter={(e) => {
            if (!prefersReducedMotion()) {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.color = COLORS.primary;
            }
          }}
          onMouseLeave={(e) => {
            if (!prefersReducedMotion() && location.pathname !== "/contact") {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.color = COLORS.white;
            } else if (location.pathname === "/contact") {
              e.currentTarget.style.transform = 'scale(1)';
            }
          }}
        >
          Contact Us
        </Link>
      </nav>
    );
  };

  // Render public navigation
  const renderPublicNav = () => (
    <nav style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: responsive.navGap,
      height: '100%',
      width: 'max-content'
    }}>
      {[
        { to: "/browse-talent", label: "Browse Talent", tourId: "browse-talent" },
        { to: "/explore-events", label: "Explore Gigs", tourId: "explore-gigs" },
        { to: "/how-it-works", label: "How It Works" },
        { to: "/our-story", label: "Our Story" },
        { to: "/contact", label: "Contact Us" }
      ].map(({ to, label, tourId }) => (
        <Link
          key={to}
          to={to}
          data-tour={tourId}
          style={{
            color: (to === "/how-it-works" && location.pathname.startsWith("/how-it-works")) || location.pathname === to ? COLORS.primary : COLORS.white,
            textDecoration: 'none',
            fontFamily: 'Montserrat, sans-serif',
            fontSize: responsive.fontSize,
            fontWeight: '600',
            transition: prefersReducedMotion() ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            padding: responsive.navPadding,
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            borderRadius: '4px',
            position: 'relative',
            transform: 'scale(1)'
          }}
          onMouseEnter={(e) => {
            if (!prefersReducedMotion()) {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.color = COLORS.primary;
            }
          }}
          onMouseLeave={(e) => {
            const isActive = (to === "/how-it-works" && location.pathname.startsWith("/how-it-works")) || location.pathname === to;
            if (!prefersReducedMotion()) {
              e.currentTarget.style.transform = 'scale(1)';
              if (!isActive) {
                e.currentTarget.style.color = COLORS.white;
              }
            }
          }}
        >
          {label}
        </Link>
      ))}
    </nav>
  );

  // Render authentication buttons
  const renderAuthButtons = () => (
    <div className="auth-buttons" style={{
      display: 'flex',
      alignItems: 'center',
      gap: responsive.buttonGap || '0.75rem',
      height: '100%'
    }}>
      <Link
        to="/login"
        style={{
          color: COLORS.white,
          padding: responsive.buttonPadding,
          borderRadius: '4px',
          fontFamily: 'Montserrat, sans-serif',
          fontSize: responsive.buttonFontSize,
          fontWeight: '600',
          textDecoration: 'none',
          transition: prefersReducedMotion() ? 'none' : 'all 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          gap: '0.4rem',
          minHeight: responsive.buttonMinHeight,
          justifyContent: 'center',
          minWidth: responsive.buttonMinWidth,
          backgroundColor: COLORS.primary,
          boxShadow: '0 2px 6px rgba(1, 162, 241, 0.2)'
        }}
        onMouseEnter={handleButtonHover(true, 'login')}
        onMouseLeave={handleButtonHover(false, 'login')}
      >
        Login
      </Link>
      <Link
        to="/register"
        style={{
          color: COLORS.white,
          padding: responsive.buttonPadding,
          borderRadius: '4px',
          fontFamily: 'Montserrat, sans-serif',
          fontSize: responsive.buttonFontSize,
          fontWeight: '600',
          textDecoration: 'none',
          transition: prefersReducedMotion() ? 'none' : 'all 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          gap: '0.4rem',
          minHeight: responsive.buttonMinHeight,
          justifyContent: 'center',
          minWidth: responsive.buttonMinWidth,
          backgroundColor: COLORS.tertiary,
          boxShadow: '0 2px 6px rgba(20, 72, 116, 0.2)'
        }}
        onMouseEnter={handleButtonHover(true, 'register')}
        onMouseLeave={handleButtonHover(false, 'register')}
      >
        Register
      </Link>
    </div>
  );

  // Render user controls - now standardized for all authenticated users
  const renderUserControls = () => (
    <>
      {/* Modern Profile & Dashboard Layout */}
      <div style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: screenSize === 'mobile' ? '0.75rem' : responsive.rightContentGap || '1.5rem',
        flexShrink: 1, // Allow shrinking when needed
        minWidth: 0 // Allow flex items to shrink below their content size
      }}>
        {/* Dashboard Button - Now responsive */}
        <Link 
          to={getDashboardUrl()}
          className="dashboard-button"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: responsive.dashboardGap || '0.6rem',
            padding: `${responsive.buttonPadding}`,
            textDecoration: 'none',
            color: COLORS.white,
            borderRadius: '50px',
            fontFamily: 'Montserrat, sans-serif',
            fontSize: responsive.buttonFontSize || '1.1rem',
            fontWeight: '700',
            letterSpacing: '0.5px',
            transition: prefersReducedMotion() ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            border: '2px solid rgba(255, 255, 255, 0.15)',
            position: 'relative',
            overflow: 'hidden',
            flexShrink: 0,
            whiteSpace: 'nowrap'
          }}
          onMouseEnter={(e) => {
            if (!prefersReducedMotion()) {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
              e.currentTarget.style.transform = 'scale(1.02)';
            }
          }}
          onMouseLeave={(e) => {
            if (!prefersReducedMotion()) {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
              e.currentTarget.style.transform = 'scale(1)';
            }
          }}
        >
          <LayoutDashboard size={responsive.iconSize || 24} />
          <span style={{ 
            display: screenSize === 'mobile' || width < 1200 ? 'none' : 'block' 
          }}>Dashboard</span>
        </Link>

        {/* User Profile Section - Enhanced responsive design */}
        <div
          className="user-menu"
          onClick={() => setShowDropdown(!showDropdown)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: responsive.userMenuGap || '0.85rem',
            paddingTop: responsive.userMenuPadding ? responsive.userMenuPadding.split(' ')[0] : '0.5rem',
            paddingBottom: responsive.userMenuPadding ? responsive.userMenuPadding.split(' ')[0] : '0.5rem',
            paddingLeft: responsive.userMenuPadding ? responsive.userMenuPadding.split(' ').pop() : '0.75rem',
            paddingRight: screenSize === 'mobile' ? '0.8rem' : '1.2rem',
            borderRadius: '50px',
            cursor: 'pointer',
            transition: prefersReducedMotion() ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            border: '2px solid rgba(255, 255, 255, 0.15)',
            position: 'relative',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            flexShrink: 0,
            minWidth: 0
          }}
          onMouseEnter={(e) => {
            if (!prefersReducedMotion()) {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
              e.currentTarget.style.transform = 'scale(1.02)';
            }
          }}
          onMouseLeave={(e) => {
            if (!prefersReducedMotion()) {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
              e.currentTarget.style.transform = 'scale(1)';
            }
          }}
      >
        {/* Profile Image with responsive sizing */}
        <div style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          {/* Glowing ring effect - responsive */}
          <div style={{
            position: 'absolute',
            width: `calc(${responsive.profileImageSize} + 4px)`,
            height: `calc(${responsive.profileImageSize} + 4px)`,
            borderRadius: '50%',
            background: userType === 'Talent' 
              ? 'linear-gradient(135deg, rgba(1, 162, 241, 0.3), rgba(1, 162, 241, 0.1))'
              : 'linear-gradient(135deg, rgba(248, 80, 8, 0.3), rgba(248, 80, 8, 0.1))',
            animation: 'pulse 3s ease-in-out infinite'
          }} />
          
          <img
            src={isAdmin ? adminIcon : (userProfilePhoto || (userType === 'Talent' ? talentIcon : organiserIcon))}
            alt={isAdmin ? "Admin Profile" : `${userType} Profile`}
            style={{
              width: responsive.profileImageSize,
              height: responsive.profileImageSize,
              borderRadius: '50%',
              objectFit: 'cover',
              border: '3px solid rgba(255, 255, 255, 0.2)',
              position: 'relative',
              zIndex: 1
            }}
          />
        </div>

        {/* User info - hide on smaller screens */}
        <div style={{ 
          display: width < 1100 ? 'none' : 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '3px',
          minWidth: 0,
          overflow: 'hidden'
        }}>
          <div style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: responsive.userNameFontSize || '1.15rem',
            fontWeight: '600',
            color: COLORS.white,
            letterSpacing: '0.3px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {userName}
          </div>
          <div style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: responsive.userRoleFontSize || '0.85rem',
            fontWeight: '500',
            color: 'rgba(255, 255, 255, 0.7)',
            letterSpacing: '1px',
            textTransform: 'uppercase'
          }}>
            {isAdmin ? "Admin" : userType}
          </div>
        </div>
        
        {/* Chevron indicator - responsive */}
        <div style={{
          display: width < 900 ? 'none' : 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: responsive.chevronSize ? `calc(${responsive.chevronSize}px + 12px)` : '32px',
          height: responsive.chevronSize ? `calc(${responsive.chevronSize}px + 12px)` : '32px',
          borderRadius: '50%',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          marginLeft: '4px',
          flexShrink: 0
        }}>
          <ChevronDown size={responsive.chevronSize || 20} color={COLORS.white} />
        </div>

        {/* Dropdown Menu */}
        <div className={`dropdown-menu-user ${showDropdown ? 'show' : ''}`} style={{
          position: 'absolute',
          right: 0,
          top: 'calc(100% + 8px)',
          width: '100%',
          backgroundColor: 'rgba(255, 255, 255, 0.75)',
          borderRadius: '15px',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.15)',
          opacity: showDropdown ? 1 : 0,
          visibility: showDropdown ? 'visible' : 'hidden',
          transform: showDropdown ? 'translateY(0)' : 'translateY(-10px)',
          transition: prefersReducedMotion() ? 'none' : 'all 0.3s ease',
          overflow: 'hidden',
          zIndex: 1001
        }}>
          {isAdmin ? (
            // Admin dropdown items
            <>
              <Link 
                to="/admin-dashboard" 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px 16px',
                  color: COLORS.textDark,
                  textDecoration: 'none',
                  fontSize: responsive.dropdownFontSize || '0.95rem',
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: '500',
                  transition: prefersReducedMotion() ? 'none' : 'all 0.2s ease',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(1, 162, 241, 0.1)';
                  e.currentTarget.style.paddingLeft = '20px';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.paddingLeft = '16px';
                }}
              >
                <Shield size={responsive.iconSize || ICON_SIZES.regular} />
                Admin Dashboard
              </Link>
              <Link 
                to="/admin-users" 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px 16px',
                  color: COLORS.textDark,
                  textDecoration: 'none',
                  fontSize: responsive.dropdownFontSize || '0.95rem',
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: '500',
                  transition: prefersReducedMotion() ? 'none' : 'all 0.2s ease',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(1, 162, 241, 0.1)';
                  e.currentTarget.style.paddingLeft = '20px';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.paddingLeft = '16px';
                }}
              >
                <UserCircle size={responsive.iconSize || ICON_SIZES.regular} />
                User Management
              </Link>
              <Link 
                to="/admin-settings" 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px 16px',
                  color: COLORS.textDark,
                  textDecoration: 'none',
                  fontSize: responsive.dropdownFontSize || '0.95rem',
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: '500',
                  transition: prefersReducedMotion() ? 'none' : 'all 0.2s ease',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(1, 162, 241, 0.1)';
                  e.currentTarget.style.paddingLeft = '20px';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.paddingLeft = '16px';
                }}
              >
                <Settings size={responsive.iconSize || ICON_SIZES.regular} />
                System Settings
              </Link>
            </>
          ) : (
            // Regular user dropdown items
            <>
              {/* Notifications */}
              <Link 
                to={`/${userType?.toLowerCase()}-notifications/${userId}`} 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px 16px',
                  color: COLORS.textDark,
                  textDecoration: 'none',
                  fontSize: responsive.dropdownFontSize || '1rem',
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: '500',
                  transition: prefersReducedMotion() ? 'none' : 'all 0.2s ease',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(1, 162, 241, 0.1)';
                  e.currentTarget.style.paddingLeft = '20px';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.paddingLeft = '16px';
                }}
              >
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <Bell size={responsive.iconSize || ICON_SIZES.regular} />
                  {unreadCounts.notifications > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: '-6px',
                      right: '-6px',
                      backgroundColor: COLORS.secondary,
                      color: COLORS.white,
                      fontSize: '0.6rem',
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '600'
                    }}>
                      {unreadCounts.notifications > 9 ? "9+" : unreadCounts.notifications}
                    </span>
                  )}
                </div>
                Notifications
              </Link>

              {/* Inbox */}
              <Link 
                to={`/${userType?.toLowerCase()}-inbox/${userId}`} 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px 16px',
                  color: COLORS.textDark,
                  textDecoration: 'none',
                  fontSize: responsive.dropdownFontSize || '1rem',
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: '500',
                  transition: prefersReducedMotion() ? 'none' : 'all 0.2s ease',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(1, 162, 241, 0.1)';
                  e.currentTarget.style.paddingLeft = '20px';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.paddingLeft = '16px';
                }}
              >
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <MessageCircle size={responsive.iconSize || ICON_SIZES.regular} />
                  {unreadCounts.messages > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: '-6px',
                      right: '-6px',
                      backgroundColor: COLORS.secondary,
                      color: COLORS.white,
                      fontSize: '0.6rem',
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '600'
                    }}>
                      {unreadCounts.messages > 9 ? "9+" : unreadCounts.messages}
                    </span>
                  )}
                </div>
                Inbox
              </Link>

              {/* Settings */}
              <Link 
                to={`/${userType?.toLowerCase()}-settings/${userId}`} 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px 16px',
                  color: COLORS.textDark,
                  textDecoration: 'none',
                  fontSize: responsive.dropdownFontSize || '1rem',
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: '500',
                  transition: prefersReducedMotion() ? 'none' : 'all 0.2s ease',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(1, 162, 241, 0.1)';
                  e.currentTarget.style.paddingLeft = '20px';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.paddingLeft = '16px';
                }}
              >
                <Settings size={responsive.iconSize || ICON_SIZES.regular} />
                Settings
              </Link>
            </>
          )}
          
          {/* Logout Button */}
          <button 
            onClick={handleLogout} 
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 16px',
              color: COLORS.secondary,
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1rem',
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: '500',
              transition: prefersReducedMotion() ? 'none' : 'all 0.2s ease',
              textAlign: 'left'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(248, 80, 8, 0.1)';
              e.currentTarget.style.paddingLeft = '20px';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.paddingLeft = '16px';
            }}
          >
            <LogOut size={responsive.iconSize || ICON_SIZES.regular} />
            Logout
          </button>
        </div>
      </div>
      </div>
    </>
  );

  // Render mobile menu
  const renderMobileMenu = () => (
    <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`} style={{
      display: mobileMenuOpen ? 'flex' : 'none',
      flexDirection: 'column',
      position: 'fixed',
      top: responsive.headerHeight || '85px', // Match header height
      left: 0,
      right: 0,
      backgroundColor: COLORS.headerBg,
      padding: 0,
      zIndex: 100001,
      borderBottomLeftRadius: '15px',
      borderBottomRightRadius: '15px',
      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
      maxHeight: `calc(100vh - ${responsive.headerHeight || '85px'})`, // Adjust for new header height
      overflowY: 'auto'
    }}>
      {isAuthenticated && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          padding: '1rem',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <img
            src={isAdmin ? adminIcon : (userProfilePhoto || (userType === 'Talent' ? talentIcon : organiserIcon))}
            alt={isAdmin ? "Admin Profile" : `${userType} Profile`}
            style={{
              width: isAdmin ? '80px' : '50px',
              height: isAdmin ? '80px' : '50px',
              borderRadius: '50%',
              objectFit: 'cover'
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '1rem',
              fontWeight: '600',
              color: 'white'
            }}>
              {userName}
            </div>
            <div style={{
              display: 'inline-block',
              padding: '0.2rem 0.5rem',
              borderRadius: '4px',
              marginTop: '0.2rem',
              backgroundColor: isAdmin ? COLORS.adminAccent : 
                               userType === "Talent" ? COLORS.talentAccent : COLORS.organiserAccent
            }}>
              <span style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '0.7rem',
                fontWeight: '600',
                color: 'white'
              }}>
                {isAdmin ? "ADMIN" : userType?.toUpperCase()}
              </span>
            </div>
          </div>
        </div>
      )}
      
      <nav style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Public Navigation Items */}
        {!isAuthenticated ? (
          // Public mobile menu
          <>
            <div style={{
              marginBottom: '1rem',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              gap: '0.75rem',
              flexWrap: 'wrap'
            }}>
              <Link
                to="/login"
                onClick={closeMobileMenu}
                style={{
                  color: COLORS.white,
                  padding: 'clamp(0.4rem, 0.9vw, 0.5rem) clamp(1rem, 2.5vw, 1.5rem)',
                  borderRadius: '4px',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: 'clamp(0.8rem, 0.9vw, 0.9rem)',
                  fontWeight: '600',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '40px',
                  width: 'calc(50% - 0.375rem)',
                  backgroundColor: COLORS.primary,
                  boxShadow: '0 2px 6px rgba(1, 162, 241, 0.2)',
                  transition: prefersReducedMotion() ? 'none' : 'all 0.3s ease'
                }}
              >
                Login
              </Link>
              <Link
                to="/register"
                onClick={closeMobileMenu}
                style={{
                  color: COLORS.white,
                  padding: 'clamp(0.4rem, 0.9vw, 0.5rem) clamp(1rem, 2.5vw, 1.5rem)',
                  borderRadius: '4px',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: 'clamp(0.8rem, 0.9vw, 0.9rem)',
                  fontWeight: '600',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '40px',
                  width: 'calc(50% - 0.375rem)',
                  backgroundColor: COLORS.tertiary,
                  boxShadow: '0 2px 6px rgba(20, 72, 116, 0.2)',
                  transition: prefersReducedMotion() ? 'none' : 'all 0.3s ease'
                }}
              >
                Register
              </Link>
            </div>
            
            {[
              { to: "/browse-talent", label: "Browse Talent", tourId: "browse-talent" },
              { to: "/explore-events", label: "Explore Gigs", tourId: "explore-gigs" },
              { to: "/how-it-works", label: "How It Works" },
              { to: "/our-story", label: "Our Story" },
              { to: "/contact", label: "Contact Us" }
            ].map(({ to, label, tourId }) => (
              <Link
                key={to}
                to={to}
                onClick={closeMobileMenu}
                data-tour={tourId}
                style={{
                  color: COLORS.white,
                  textDecoration: 'none',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: responsive.dropdownFontSize || '0.95rem',
                  fontWeight: '600',
                  padding: '0.9rem',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {label}
              </Link>
            ))}
          </>
        ) : (
          // Authenticated mobile menu
          <>
            {/* Dashboard Link */}
            <Link 
              to={getDashboardUrl()} 
              onClick={closeMobileMenu}
              style={{
                color: themeColors.primary,
                textDecoration: 'none',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '0.95rem',
                fontWeight: '600',
                padding: '0.9rem',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: themeColors.primary === COLORS.primary ? 'rgba(1, 162, 241, 0.1)' : 'rgba(248, 80, 8, 0.1)'
              }}
            >
              <LayoutDashboard size={18} />
              {isAdmin ? "Admin Dashboard" : "Dashboard"}
            </Link>

            {/* Standard navigation for authenticated users */}
            <>
              <Link
                to="/browse-talent"
                onClick={closeMobileMenu}
                data-tour="browse-talent"
                style={{
                  color: COLORS.white,
                  textDecoration: 'none',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: responsive.dropdownFontSize || '0.95rem',
                  fontWeight: '600',
                  padding: '0.9rem',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                Browse Talent
              </Link>
              
              <Link
                to="/explore-events"
                onClick={closeMobileMenu}
                data-tour="explore-gigs"
                style={{
                  color: COLORS.white,
                  textDecoration: 'none',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: responsive.dropdownFontSize || '0.95rem',
                  fontWeight: '600',
                  padding: '0.9rem',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                Explore Gigs
              </Link>
              
              <Link 
                to="/how-it-works" 
                onClick={closeMobileMenu}
                style={{
                  color: COLORS.white,
                  textDecoration: 'none',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: responsive.dropdownFontSize || '0.95rem',
                  fontWeight: '600',
                  padding: '0.9rem',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                How It Works
              </Link>
              
              <Link 
                to="/our-story" 
                onClick={closeMobileMenu}
                style={{
                  color: COLORS.white,
                  textDecoration: 'none',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: responsive.dropdownFontSize || '0.95rem',
                  fontWeight: '600',
                  padding: '0.9rem',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                Our Story
              </Link>
              
              <Link 
                to="/contact" 
                onClick={closeMobileMenu}
                style={{
                  color: COLORS.white,
                  textDecoration: 'none',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: responsive.dropdownFontSize || '0.95rem',
                  fontWeight: '600',
                  padding: '0.9rem',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                Contact Us
              </Link>
            </>
            
            {/* Notification and Message Links - Now always visible for non-admin authenticated users */}
            {!isAdmin && (
              <>
                <Link 
                  to={`/${userType?.toLowerCase()}-notifications/${userId}`} 
                  onClick={closeMobileMenu}
                  style={{
                    color: COLORS.white,
                    textDecoration: 'none',
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: responsive.dropdownFontSize || '0.95rem',
                    fontWeight: '600',
                    padding: '0.9rem',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <div style={{ position: 'relative', display: 'inline-flex' }}>
                    <Bell size={18} />
                    {unreadCounts.notifications > 0 && (
                      <span style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        backgroundColor: COLORS.secondary,
                        color: COLORS.white,
                        fontSize: '0.7rem',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {unreadCounts.notifications > 9 ? "9+" : unreadCounts.notifications}
                      </span>
                    )}
                  </div>
                  Notifications
                </Link>
                
                <Link 
                  to={`/${userType?.toLowerCase()}-inbox/${userId}`} 
                  onClick={closeMobileMenu}
                  style={{
                    color: COLORS.white,
                    textDecoration: 'none',
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: responsive.dropdownFontSize || '0.95rem',
                    fontWeight: '600',
                    padding: '0.9rem',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <div style={{ position: 'relative', display: 'inline-flex' }}>
                    <MessageCircle size={18} />
                    {unreadCounts.messages > 0 && (
                      <span style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        backgroundColor: COLORS.secondary,
                        color: COLORS.white,
                        fontSize: '0.7rem',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {unreadCounts.messages > 9 ? "9+" : unreadCounts.messages}
                      </span>
                    )}
                  </div>
                  Messages
                </Link>
              </>
            )}
            
            {/* Settings Link - All authenticated users */}
            <Link 
              to={`/${userType?.toLowerCase()}-settings/${userId}`} 
              onClick={closeMobileMenu}
              style={{
                color: COLORS.white,
                textDecoration: 'none',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '0.95rem',
                fontWeight: '600',
                padding: '0.9rem',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Settings size={18} />
              Settings
            </Link>
            
            {/* ChatBot Button - Mobile Only */}
            <button
              onClick={() => {
                setChatBotOpen(true);
                closeMobileMenu();
              }}
              style={{
                color: COLORS.white,
                textDecoration: 'none',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '0.95rem',
                fontWeight: '600',
                padding: '0.9rem',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left'
              }}
            >
              <Bot size={18} />
              GiGBot Assistant
            </button>
            
            {/* Logout Button - All authenticated users */}
            <button 
              onClick={handleLogout} 
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                width: '100%',
                padding: '0.9rem',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                color: COLORS.secondary,
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '0.95rem',
                fontWeight: '600',
                textAlign: 'left',
                cursor: 'pointer'
              }}
            >
              <LogOut size={18} />
              Logout
            </button>
          </>
        )}
      </nav>
    </div>
  );

  // Don't render header until auth is ready (except for public)
  if (!authReady && headerType !== 'public') {
    return null;
  }

  return (
    <header
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: responsive.headerPadding || "0.3rem 2rem",
        background: isScrolled ? COLORS.headerBg : "transparent",
        color: COLORS.white,
        boxShadow: isScrolled ? "0 2px 8px rgba(0, 0, 0, 0.1)" : "none",
        transition: prefersReducedMotion() ? "none" : "background-color 0.3s ease, box-shadow 0.3s ease",
        zIndex: 1000,
        minHeight: responsive.headerHeight || "85px",
      }}
    >
      {/* Logo Section */}
      <Link to={getLogoLink()} style={{ 
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
        height: "100%",
        cursor: "pointer",
        flexShrink: 0
      }}>
        <img
          src={logo}
          alt="GIGiFY logo"
          style={{
            height: responsive.logoHeight,
            transition: prefersReducedMotion() ? "none" : "transform 0.3s ease",
            display: "block",
            cursor: "pointer",
            width: "auto",
            marginTop: "8px"
          }}
          onMouseEnter={handleLogoHover(true)}
          onMouseLeave={handleLogoHover(false)}
        />
      </Link>

      {/* Mobile Menu Toggle */}
      <div style={{
        display: isMobileLayout ? "flex" : "none",
        alignItems: "center",
        height: "100%",
        gap: "0.5rem"
      }}>
        {/* Dashboard Icon for Mobile */}
        {isAuthenticated && (
          <Link
            to={getDashboardUrl()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0.5rem',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '0.5rem',
              transition: 'all 0.2s ease',
              backgroundColor: 'transparent'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.8';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            <LayoutDashboard size={24} />
          </Link>
        )}
        <HamburgerButton
          isOpen={mobileMenuOpen}
          onClick={toggleMobileMenu}
          size={32}
          ariaLabel={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
        />
      </div>

      {/* Mobile Menu */}
      {renderMobileMenu()}

      {/* Desktop Navigation - Standard Layout */}
      <div style={{
        display: isMobileLayout ? 'none' : 'flex',
        alignItems: 'center',
        height: '100%'
      }}>
        {headerType === 'public' && renderPublicNav()}
        {headerType === 'authenticated' && renderAuthenticatedNav()}
      </div>

      {/* Right Side Content */}
      <div style={{
        display: isMobileLayout ? 'none' : 'flex',
        alignItems: 'center',
        gap: responsive.rightContentGap || '1rem',
        height: '100%'
      }}>
        {headerType === 'public' ? renderAuthButtons() : (
          <>
            {renderUserControls()}
          </>
        )}
      </div>

      <style>{`
        /* Animation Keyframes */
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
        
        /* Common Navigation Styles */
        .nav-link {
          color: ${COLORS.white};
          text-decoration: none;
          font-family: Montserrat, sans-serif;
          font-size: clamp(0.8rem, 1vw, 1rem);
          font-weight: 600;
          transition: all 0.3s ease;
          position: relative;
          display: flex;
          align-items: center;
          gap: 0.4rem;
          white-space: nowrap;
          padding: 0.5rem 0.8rem;
          border-radius: 4px;
        }
        
        .nav-link:hover {
          color: ${COLORS.primary};
          background-color: rgba(1, 162, 241, 0.1);
        }
        
        .notification-badge {
          position: absolute;
          top: -8px;
          right: -8px;
          background-color: ${COLORS.secondary};
          color: ${COLORS.white};
          font-size: 0.75rem;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: pulse 2s infinite;
        }
        
        /* Media Queries */
        @media (max-width: 768px) {
          header {
            padding: 0.3rem 1rem !important;
          }
        }
        
        /* Small Mobile Tweaks - Removed logo height override to allow responsive sizing */
      `}</style>
      
      {/* ChatBot Component - Only render on mobile when triggered */}
      {isMobileLayout && (
        <ChatBot 
          isOpenExternal={chatBotOpen}
          onCloseExternal={() => setChatBotOpen(false)}
        />
      )}

    </header>
  );
});

UnifiedHeader.displayName = 'UnifiedHeader';

export default UnifiedHeader;
            